
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { EnhancedDocumentType } from '@/components/documents/enhanced-document-types';
import { AssessmentFormData } from '@/components/business-assessment/AssessmentForm';

export function useAssessmentEnhancedCreation() {
  const [isCreating, setIsCreating] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const createDocument = async (documentType: EnhancedDocumentType) => {
    try {
      setIsCreating(true);

      // Get current user session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("No active session");
      }

      // Get user's comprehensive assessment data for pre-filling
      const { data: assessment } = await supabase
        .from('business_assessments')
        .select('data')
        .eq('user_id', session.user.id)
        .single();

      const assessmentData = assessment?.data ? assessment.data as unknown as AssessmentFormData : undefined;

      // Create enhanced initial content based on comprehensive assessment
      const enhancedContent = getEnhancedInitialContent(documentType, assessmentData);

      const { data, error } = await supabase
        .from('documents')
        .insert({
          user_id: session.user.id,
          title: documentType.title,
          type: documentType.id as any,
          content: enhancedContent
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Document Created",
        description: assessmentData 
          ? "Document pre-filled with your comprehensive business assessment data for highly personalized content!"
          : "Document created successfully. Complete your comprehensive assessment for personalized strategic content.",
      });

      navigate(`/documents/${data.id}`);
    } catch (error: any) {
      console.error('Error creating document:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create document. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsCreating(false);
    }
  };

  return { createDocument, isCreating };
}

function getEnhancedInitialContent(documentType: EnhancedDocumentType, assessmentData?: AssessmentFormData) {
  const baseContent = documentType.sections?.reduce((acc, section) => {
    const fieldName = section.title.toLowerCase().replace(/\s+/g, '').replace(/&/g, '');
    acc[fieldName] = '';
    return acc;
  }, {} as Record<string, string>) || {};

  if (!assessmentData) return baseContent;

  const enhanced = { ...baseContent };

  // Helper function to safely get assessment property with fallback
  const safeGet = (obj: any, key: string, fallback = '') => {
    return obj && typeof obj === 'object' && key in obj ? obj[key] : fallback;
  };

  // Enhanced content generation using comprehensive assessment data
  const companyName = safeGet(assessmentData, 'companyName', 'Your Company');
  const industry = safeGet(assessmentData, 'industry');
  const businessStage = safeGet(assessmentData, 'businessStage');
  const employeeCount = safeGet(assessmentData, 'employeeCount');
  const annualRevenue = safeGet(assessmentData, 'annualRevenue');
  const businessDescription = safeGet(assessmentData, 'businessDescription');
  const targetMarket = safeGet(assessmentData, 'targetMarket');
  const competitiveAdvantage = safeGet(assessmentData, 'competitiveAdvantage');
  const challenges = safeGet(assessmentData, 'challenges');
  const goals = safeGet(assessmentData, 'goals');
  const revenueModel = safeGet(assessmentData, 'revenueModel');
  const marketingChannels = safeGet(assessmentData, 'marketingChannels');
  const keyMetrics = safeGet(assessmentData, 'keyMetrics');

  // Business Strategy Documents with comprehensive data
  if (documentType.id === 'business_plan') {
    enhanced.executiveSummary = `${companyName} is a ${businessStage} ${industry} company with ${employeeCount} employees, generating ${annualRevenue} in annual revenue. ${businessDescription}`;
    enhanced.companyOverview = `${companyName} operates in the ${industry} sector, serving ${targetMarket}. Our competitive advantage lies in ${competitiveAdvantage}. We currently employ ${employeeCount} and operate on a ${revenueModel} revenue model.`;
    enhanced.marketAnalysis = `Target Market: ${targetMarket}. Key marketing channels include ${marketingChannels}. We track performance using metrics such as ${keyMetrics}.`;
    enhanced.challenges = `Current business challenges include: ${challenges}`;
    enhanced.objectives = `Our primary business goals for the next 12 months: ${goals}`;
    enhanced.financialProjections = `Current revenue: ${annualRevenue}. Revenue model: ${revenueModel}. Key metrics: ${keyMetrics}`;
  }

  if (documentType.id === 'strategic_plan') {
    enhanced.visionStatement = `Strategic vision aligned with goals: ${goals}`;
    enhanced.currentSituation = `Current situation: ${businessDescription}. Stage: ${businessStage}. Key challenges: ${challenges}`;
    enhanced.strategicObjectives = `Building on our ${industry} expertise with ${employeeCount} team members to achieve: ${goals}`;
    enhanced.competitiveAnalysis = `Our competitive advantage: ${competitiveAdvantage}. Market position in ${industry} sector.`;
  }

  // Financial Documents with enhanced data
  if (documentType.id === 'financial_forecast') {
    enhanced.revenueProjections = `Based on current ${annualRevenue} revenue and ${revenueModel} model, projecting growth in ${industry} sector. Key metrics: ${keyMetrics}`;
    enhanced.expenseForecasts = `Operating expenses for ${employeeCount} employees in ${industry}. Marketing spend across ${marketingChannels}`;
    enhanced.assumptions = `Business stage: ${businessStage}. Target market: ${targetMarket}. Growth goals: ${goals}`;
  }

  if (documentType.id === 'budget_template') {
    enhanced.budgetSummary = `Annual budget planning for ${companyName} (${businessStage}) in ${industry} with ${employeeCount} employees and ${annualRevenue} revenue`;
    enhanced.departmentalBudgets = `Budget allocations based on ${annualRevenue} revenue and ${revenueModel} model. Marketing budget for ${marketingChannels}`;
    enhanced.goals = `Budget supports business goals: ${goals}`;
  }

  // Technology Documents
  if (documentType.id === 'tech_architecture') {
    enhanced.systemOverview = `Technology architecture for ${companyName} in ${industry}. Supporting ${employeeCount} employees and ${targetMarket}`;
    enhanced.scalabilityPlan = `Scaling technology to support business goals: ${goals}. Current stage: ${businessStage}`;
    enhanced.challenges = `Technical challenges related to: ${challenges}`;
  }

  // Operations Documents
  if (documentType.id === 'process_optimization') {
    enhanced.currentProcessAnalysis = `Current operational processes for ${employeeCount} team members in ${industry}. Business stage: ${businessStage}`;
    enhanced.inefficiencyIdentification = `Key operational challenges: ${challenges}`;
    enhanced.improvementOpportunities = `Optimization opportunities to achieve: ${goals}`;
  }

  // Product Documents
  if (documentType.id === 'product_roadmap') {
    enhanced.productVision = `Product vision for ${companyName} serving ${targetMarket}. Goals: ${goals}`;
    enhanced.marketRequirements = `Market needs based on ${targetMarket} and competitive advantage: ${competitiveAdvantage}`;
    enhanced.resourceRequirements = `Resource planning for ${employeeCount} team members. Revenue model: ${revenueModel}`;
  }

  return enhanced;
}
